import { gql, useQuery } from '@apollo/client'

import {
  ConsoleUserOwnedItemsPortfolioFragmentDoc,
  ConsoleUserOwnedItemsQuery,
  ConsoleUserOwnedItemsQueryVariables,
  ItemType,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { useCurrentUser } from '@/core/auth'
import { spreadPortfoliosBySku } from '@/core/sku'

export type State = ReturnType<typeof useViewModel>['state']
export type Action = ReturnType<typeof useViewModel>['actions']

export const useViewModel = () => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ userId: string }>()
  const { data, loading, fetchMore, refetch } = useQuery<
    ConsoleUserOwnedItemsQuery,
    ConsoleUserOwnedItemsQueryVariables
  >(QUERY, {
    variables: {
      userId: router.query.userId,
      type: ItemType.Ticket,
    },
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
  })

  return {
    state: {
      userId: router.query.userId,
      loading,
      filter: ItemType.Ticket,
      artistId: user?.uid ?? '',
      hasNextPage: data?.user?.artistPortfolios?.pageInfo?.hasNextPage,
      portfolios: spreadPortfoliosBySku(data?.user?.artistPortfolios ?? null),
    },
    actions: {
      async onFilter(filter: ItemType | 'general') {
        refetch({
          type: filter === 'general' ? null : filter,
        })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            pageInput: {
              limit: 10,
              cursor: data?.user?.artistPortfolios?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
}

const QUERY = gql`
  query consoleUserOwnedItems(
    $userId: ID!
    $type: ItemType
    $pageInput: PageInput
  ) {
    user(id: $userId) {
      id
      artistPortfolios(type: $type, pageInput: $pageInput) {
        ...ConsoleUserOwnedItemsPortfolio
      }
    }
  }
  ${ConsoleUserOwnedItemsPortfolioFragmentDoc}
`
